/**
 * The four headline numbers.
 *
 * ---- Why these are derived here and not read from the server ----
 *
 * `analytics.get_pipeline_analytics` returns exactly these aggregates and we rendered them
 * directly at first. Its date filtering is broken: **`from_date` is ignored whenever `to_date` is
 * also supplied.** Measured on rfqdev 2026-08-11 —
 *
 *     from_date=2026-08-11&to_date=2026-08-11  → total_enquiries 24   (truth: 1)
 *     from_date=2026-08-08&to_date=2026-08-11  → total_enquiries 24   (truth: 3)
 *     from_date=2026-08-08                     → total_enquiries  3   (correct alone)
 *
 * Every range preset sends both bounds, so these tiles answered "all time" whichever range was
 * picked, while the funnel and workload below them answered the range correctly — the range
 * buttons looked broken because half the page ignored them. Deriving from the same filtered facts
 * makes every number on the screen agree by construction.
 *
 * Filed for the Frappe team; when their range filter is fixed this can go back to the method,
 * whose definitions `buildKpis` deliberately matches.
 */
import type { Kpis } from '../../lib/analytics/aggregate'
import { pct } from './charts'

/**
 * A tile.
 *
 * `sub` carries the raw counts behind the number — "1 of 24" — because these samples are small
 * enough that a bare percentage misleads: 100% of one decided enquiry is not a track record.
 */
function Stat({
  label, value, sub, delta,
}: {
  label: string
  value: string
  sub?: string
  delta?: { diff: number; unit: 'pct' | 'days' } | null
}) {
  return (
    <div className="card pad stat" style={{ flex: 1, minWidth: 150 }}>
      <div className="k">{label}</div>
      <div className="v">{value}</div>
      {delta === undefined || delta === null ? (
        <div className="d">{sub ?? ' '}</div>
      ) : (
        <div className={delta.diff > 0 ? 'd up' : delta.diff < 0 ? 'd down' : 'd'}>
          {delta.diff === 0
            ? 'no change'
            : `${delta.diff > 0 ? '▲' : '▼'} ${
                delta.unit === 'pct'
                  ? pct(Math.abs(delta.diff))
                  : `${Math.abs(delta.diff).toFixed(1)}d`
              }`}
          {sub ? <span style={{ opacity: 0.75 }}> · {sub}</span> : null}
        </div>
      )}
    </div>
  )
}

/** Only compare when the previous period actually held enquiries — otherwise every first period
 *  would show a spurious improvement over nothing. */
function delta(
  current: number | null,
  previous: number | null,
  previousTotal: number,
  unit: 'pct' | 'days',
) {
  if (current === null || previous === null || previousTotal === 0) return null
  const diff = Number((current - previous).toFixed(unit === 'pct' ? 4 : 1))
  return { diff, unit }
}

export function KpiRow({
  kpis, previous, isLoading, error, blockedCount,
}: {
  kpis: Kpis | undefined
  /** The same KPIs over the preceding equal-length window, or null when there is no such window. */
  previous: Kpis | null
  isLoading: boolean
  error: unknown
  blockedCount: number
}) {
  if (isLoading) {
    return (
      <div className="card pad" style={{ color: 'var(--muted)', fontSize: 12.5 }}>
        Loading pipeline summary…
      </div>
    )
  }

  if (error || !kpis) {
    return (
      <div className="card pad" style={{ color: 'var(--danger)', fontSize: 12.5 }}>
        {error instanceof Error ? error.message : 'Could not load the pipeline summary.'}
      </div>
    )
  }

  const prevTotal = previous?.total ?? 0

  return (
    <div className="row" style={{ flexWrap: 'wrap' }}>
      <Stat
        label="Conversion rate"
        value={pct(kpis.conversion_rate)}
        sub={`${kpis.won} of ${kpis.total}`}
        delta={delta(kpis.conversion_rate, previous?.conversion_rate ?? null, prevTotal, 'pct')}
      />
      <Stat
        label="Win rate"
        // Null is "nothing has been decided", which is not 0% — that would read as losing
        // everything, when in fact nothing has concluded.
        value={kpis.win_rate === null ? '—' : pct(kpis.win_rate)}
        sub={kpis.decided > 0 ? `${kpis.won} of ${kpis.decided} decided` : 'none decided yet'}
        delta={delta(kpis.win_rate, previous?.win_rate ?? null, prevTotal, 'pct')}
      />
      <Stat
        label="Avg cycle time"
        value={kpis.avg_cycle_days === null ? '—' : `${kpis.avg_cycle_days.toFixed(1)}d`}
        sub="creation → last status change"
        delta={delta(kpis.avg_cycle_days, previous?.avg_cycle_days ?? null, prevTotal, 'days')}
      />
      <Stat
        label="Open requirements"
        value={String(kpis.open)}
        sub={`${blockedCount} blocked`}
      />
    </div>
  )
}
